// store/financials/slice.ts

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FinancialsState } from '@/types/financials';
import { getFinancials } from './thunks';

const initialState: FinancialsState = {
  // Core data
  financialsData: [],

  // Loading states
  isLoadingFinancials: false,

  // Error states
  financialsError: null,

  // Filters (for caching/state management)
  lastFinancialsFilters: null,

  // Metadata
  financialsCount: 0,
  lastUpdated: null,
};

export const financialsSlice = createSlice({
  name: 'financials',
  initialState,
  reducers: {
    // ------------------------------------ CLEAR ACTIONS ------------------------------------

    clearFinancialsError: state => {
      state.financialsError = null;
    },

    // ------------------------------------ FILTER ACTIONS ------------------------------------

    setFinancialsFilters: (state, action: PayloadAction<any>) => {
      state.lastFinancialsFilters = action.payload;
    },

    clearFinancialsFilters: state => {
      state.lastFinancialsFilters = null;
    },

    // ------------------------------------ RESET ACTIONS ------------------------------------

    resetFinancials: state => {
      state.financialsData = [];
      state.financialsCount = 0;
      state.lastFinancialsFilters = null;
      state.financialsError = null;
    },

    resetFinancialsState: () => initialState,
  },
  extraReducers: builder => {
    builder
      // --------------------------------------- FINANCIALS DATA ---------------------------------------

      // Get Financials Data
      .addCase(getFinancials.pending, state => {
        state.isLoadingFinancials = true;
        state.financialsError = null;
      })
      .addCase(getFinancials.fulfilled, (state, action) => {
        state.isLoadingFinancials = false;
        state.financialsError = null;
        state.financialsData = action.payload;
        state.financialsCount = action.payload.length;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(getFinancials.rejected, (state, action) => {
        state.isLoadingFinancials = false;
        state.financialsError = action.payload as string;
      });
  },
});

export const {
  clearFinancialsError,
  setFinancialsFilters,
  clearFinancialsFilters,
  resetFinancials,
  resetFinancialsState,
} = financialsSlice.actions;

export const financialsReducer = financialsSlice.reducer;
